import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import PricingPage from "@/pages/PricingPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import AdminClientsPage from "@/pages/AdminClientsPage";
import DashboardPage from "@/pages/DashboardPage";
import VaultsPage from "@/pages/VaultsPage";
import BubblesPage from "@/pages/BubblesPage";
import ObligationsPage from "@/pages/ObligationsPage";
import CounterpartiesPage from "@/pages/CounterpartiesPage";
import InsightsPage from "@/pages/InsightsPage";
import SentinelPage from "@/pages/SentinelPage";
import BankPage from "@/pages/BankPage";
import ReportPage from "@/pages/ReportPage";
import IngestPage from "@/pages/IngestPage";
import DataPoolPage from "@/features/datapool/DataPoolPage";
import DataPoolV2Page from "@/features/datapool_v2/DataPoolV2Page";
import DemoPage from "@/pages/DemoPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CompanyProfilePage from "@/pages/CompanyProfilePage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import TrialExpiredPage from "@/pages/TrialExpiredPage";

// Access statuses that are allowed past ProtectedRoute. "active" and
// "trialing" pass; everything else (trial_expired, past_due,
// cancelled, blocked) is redirected to /trial-expired. Admins are
// always allowed regardless of access_status.
const ALLOWED_ACCESS_STATUSES = new Set(["active", "trialing", null, undefined]);

function ProtectedRoute({ children }) {
  const { token, user, accessStatus } = useAuth();
  if (!token) return <Navigate to="/" replace />;
  const isAdmin = user?.role === "admin" || user?.is_admin === true;
  if (!isAdmin && !ALLOWED_ACCESS_STATUSES.has(accessStatus)) {
    return <Navigate to="/trial-expired" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { token, user } = useAuth();
  // Server enforces role re-check on every admin call. Client-side
  // we redirect non-admins so they don't see a broken page.
  //
  // Important: if the visitor isn't authenticated AT ALL, send them
  // to /login (not /dashboard). /dashboard is itself ProtectedRoute
  // and would silently bounce an anonymous visitor to /, making this
  // route look broken when the user is simply signed out.
  if (!token) {
    return <Navigate to="/login?next=/admin" replace />;
  }
  const isAdmin = user?.role === "admin" || user?.is_admin === true;
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/trial-expired" element={<TrialExpiredPage />} />
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/admin/clients" element={<AdminRoute><AdminClientsPage /></AdminRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="vaults" element={<VaultsPage />} />
            <Route path="bubbles" element={<BubblesPage />} />
            <Route path="obligations" element={<ObligationsPage />} />
            <Route path="counterparties" element={<CounterpartiesPage />} />
            <Route path="insights" element={<InsightsPage />} />
            <Route path="sentinel" element={<SentinelPage />} />
            <Route path="bank" element={<BankPage />} />
            <Route path="profile" element={<CompanyProfilePage />} />
            <Route path="report" element={<ReportPage />} />
            <Route path="ingest" element={<Navigate to="/dashboard/data-pool" replace />} />
            <Route path="data-import" element={<Navigate to="/dashboard/data-pool" replace />} />
            <Route path="data-pool" element={<DataPoolPage />} />
            <Route path="data-pool-v2" element={<DataPoolV2Page />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
